import { useUpdatePin } from '@lessonup/pin-renderer';
import {
  BodyText,
  Button,
  color,
  IconComponentImage,
  IconImageDetach,
  IconRemove,
  IconReplace,
  RangeSlider,
  SpaceBetween,
  spacing,
  styled,
} from '@lessonup/ui-components';
import React, { useRef } from 'react';
import { useEditorContext } from '../../../../../context/EditorContext/EditorContext';
import { useNonNullActivePin, usePinSettingsHandler } from '../../../../../context/EditorContext/hooks/useEditorState';
import { useEditorTranslation } from '../../../../../hooks/useEditorTranslation';
import {
  backgroundImageDetachActions,
  setPinBackgroundImageAction,
  setPinTransparencyAction,
} from '../../../../../utils/pin/sharedPinSettings';
import { useEditorUploadsStatus } from '../../../../../utils/yjs/EditorUploadsYjs/useEditorUploads';
import { useEditorContextSidebarActions } from '../../../hooks/useEditorContextSidebar';
import { ImagePreview } from '../../SharedSettings/ImagePreview';
import { useHandlePinBackgroundImageChange } from '../hooks/useHandlePinBackgroundImageChange';

export const PinBackgroundSettings: React.FC = () => {
  const { t } = useEditorTranslation();
  const pin = useNonNullActivePin();
  const pinSettings = usePinSettingsHandler();
  const isUploadPending = useEditorUploadsStatus();
  const { setModeSelecting } = useEditorContext();
  const backgroundImage = pinSettings.backgroundImage;
  const dispatch = useUpdatePin();
  const backgroundImageButtonRef = useRef<HTMLButtonElement>(null);
  const handleBackgroundImageChange = useHandlePinBackgroundImageChange(backgroundImageButtonRef);
  const isThemeBackgroundImage = !!backgroundImage && !pin.settings.backgroundImage;

  useEditorContextSidebarActions({
    setBackgroundImage: handleBackgroundImageChange,
  });

  if (!backgroundImage) {
    return (
      <SpaceBetween direction="y" spacing={spacing.size16}>
        <Button
          ref={backgroundImageButtonRef}
          buttonType="neutral"
          showStroke
          onClick={handleBackgroundImageChange}
          iconStart={<IconComponentImage />}
          loading={isUploadPending({ type: 'pin', pinId: pin.id })}
        >
          {t('contextSidebar.settings.backgroundImage.selectImage')}
        </Button>
      </SpaceBetween>
    );
  }

  const handleBackgroundImageDetach = async () => {
    const actions = await backgroundImageDetachActions(pin, backgroundImage, setModeSelecting);
    dispatch(actions);
  };

  return (
    <SpaceBetween direction="y" spacing={spacing.size16}>
      <ImagePreview url={backgroundImage} />
      <SpaceBetween direction="x" spacing={spacing.size4}>
        <Button
          ref={backgroundImageButtonRef}
          buttonType="neutral"
          showStroke
          onClick={handleBackgroundImageChange}
          loading={isUploadPending({ type: 'pin', pinId: pin.id })}
          iconStart={<IconReplace />}
        >
          {t('contextSidebar.settings.backgroundImage.replaceImage')}
        </Button>
        {!isThemeBackgroundImage && (
          <Button buttonType="neutral" onClick={handleBackgroundImageDetach} iconStart={<IconImageDetach />}>
            {t('contextSidebar.settings.backgroundImage.detachImage')}
          </Button>
        )}
      </SpaceBetween>
      <RangeSlider
        id={`${pin.id}-image-transparency-setting`}
        label={t('contextSidebar.settings.transparency')}
        onChange={(event) => dispatch(setPinTransparencyAction(event))}
        value={pinSettings.backgroundTransparency}
      />
      {!isThemeBackgroundImage ? (
        <Button
          buttonType="negative"
          onClick={() => dispatch(setPinBackgroundImageAction(undefined))}
          iconStart={<IconRemove />}
        >
          {t('contextSidebar.settings.backgroundImage.removeImage')}
        </Button>
      ) : (
        <StyledDisabledRemoveSection>
          <Button buttonType="negative" disabled={true} iconStart={<IconRemove />}>
            {t('contextSidebar.settings.backgroundImage.removeImage')}
          </Button>
          <BodyText size="small">{t('contextSidebar.removeDisabledThemeBackgroundImage')}</BodyText>
        </StyledDisabledRemoveSection>
      )}
    </SpaceBetween>
  );
};

const StyledDisabledRemoveSection = styled.div`
  color: ${color.additional.disabled.text};
`;
